import React, { createContext, useState, useEffect, useContext } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../firebase';
import {
  registerUser,
  loginUser,
  logoutUser,
  signInWithGoogle,
  signInWithApple,
  getUserData,
  reauthenticateCurrentUser,
  deleteCurrentUser,
  getPrimaryAuthProvider,
} from '../services/authService';
import { subscribeToUserData } from '@coparent/shared/src/services/authService';
import useSessionPing from '../hooks/useSessionPing';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Stamp a daily session ping once the user's uid is known. Fire-and-forget.
  useSessionPing(currentUser?.uid);

  useEffect(() => {
    let unsubUserData = null;
    let fallbackTimer = null;

    const cleanupUserDataSub = () => {
      if (unsubUserData) {
        unsubUserData();
        unsubUserData = null;
      }
      if (fallbackTimer) {
        clearTimeout(fallbackTimer);
        fallbackTimer = null;
      }
    };

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      cleanupUserDataSub();

      if (user) {
        setUserData(null);
        let firstSnapshot = true;
        // Safety net: if Firestore is unreachable and no snapshot ever fires,
        // stop blocking the UI after 10s so the user isn't stuck on a spinner.
        fallbackTimer = setTimeout(() => {
          if (firstSnapshot) {
            firstSnapshot = false;
            setLoading(false);
          }
        }, 10000);

        unsubUserData = subscribeToUserData(user.uid, (data) => {
          setUserData(data);
          if (firstSnapshot) {
            firstSnapshot = false;
            if (fallbackTimer) {
              clearTimeout(fallbackTimer);
              fallbackTimer = null;
            }
            setLoading(false);
          }
        });
      } else {
        setUserData(null);
        setLoading(false);
      }
    }, (error) => {
      console.error('Auth state error:', error);
      setLoading(false);
    });

    return () => {
      cleanupUserDataSub();
      unsubscribe();
    };
  }, []);

  const signup = async (email, password, displayName, attribution = null, foundVia = null) => {
    return await registerUser(email, password, displayName, attribution, foundVia);
  };

  const login = async (email, password) => {
    return await loginUser(email, password);
  };

  const loginWithGoogle = async (foundVia = null) => {
    return await signInWithGoogle(foundVia);
  };

  const loginWithApple = async (foundVia = null) => {
    return await signInWithApple(foundVia);
  };

  const logout = async () => {
    return await logoutUser();
  };

  const reauthenticate = async (password) => {
    return await reauthenticateCurrentUser(password);
  };

  const deleteAccount = async () => {
    return await deleteCurrentUser();
  };

  const refreshUserData = async (uid) => {
    const userId = uid || currentUser?.uid;
    if (userId) {
      const data = await getUserData(userId);
      setUserData(data);
    }
  };

  const value = {
    currentUser,
    userData,
    signup,
    login,
    loginWithGoogle,
    loginWithApple,
    logout,
    reauthenticate,
    deleteAccount,
    getPrimaryAuthProvider,
    refreshUserData,
    loading
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="mt-3 text-gray-500 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
